import { supabase } from './api'
import type { LinkRow } from './linksApi'

/**
 * Issues — the Operations Issues Resolution Form, folded into the app.
 *
 * Readable and reportable by everyone with a valid link; triage (status,
 * classification, archive) is admin-only and re-checked in the database by
 * `require_admin`, so reaching these calls is not the same as being allowed to
 * use them.
 *
 * See 0011_issues.sql for the schema and why the abandoned EOS table was
 * adopted rather than replaced.
 */

export type Urgency = 'Low' | 'Medium' | 'High'
export type IssueStatus = 'Identified' | 'Discussing' | 'Solved'

export const URGENCIES: readonly Urgency[] = ['Low', 'Medium', 'High']
export const ISSUE_STATUSES: readonly IssueStatus[] = [
  'Identified',
  'Discussing',
  'Solved',
]

/** Selected-state colour of each status on its toggle — same tones as tasks. */
export const ISSUE_STATUS_TONE: Record<IssueStatus, 'todo' | 'working' | 'done'> = {
  Identified: 'todo',
  Discussing: 'working',
  Solved: 'done',
}

/**
 * The departments an issue can be filed under — the shop's real ones (Scott,
 * 2026-10-22), shown as a chip row, Operations pre-selected. Human Resources is
 * kept for reports about people. The column is free text in the database, so
 * older issues may still carry a retired value such as "Shop" until an admin
 * reclassifies them.
 *
 * The report form no longer asks where an issue happened: "Where?" and "Which
 * area?" overlapped (Shop and Front office were in both) and 12 of the first 15
 * reports skipped it. `issues.location` stays, and older values still show.
 */
export const DEPARTMENTS = ['Operations', 'Sales', 'Human Resources'] as const

export interface Issue {
  id: string
  title: string
  description: string
  department: string
  location: string | null
  occurred_at: string | null
  impact: string | null
  urgency: Urgency
  proposed_solution: string | null
  follow_up_required: boolean
  status: IssueStatus
  resolution_notes: string | null
  solved_at: string | null
  reported_by_id: string | null
  reported_by_name: string
  /** True when the signed-in person is the one who reported it. */
  mine: boolean
  created_at: string
  /**
   * Days since it was reported, computed by Postgres in America/New_York.
   * Same rule as tasks' `days_until`: the browser never works this out, or a
   * phone on Pacific time would disagree with the shop.
   */
  days_open: number
  archived: boolean
  /** Live (unarchived) tasks spawned from this issue. */
  task_count: number
  /** Set when this issue was dropped down from a headline. Null otherwise. */
  headline_id: string | null
  headline_body: string | null
  /** URLs attached to this issue. Empty array when there are none. */
  links: LinkRow[]
  /**
   * EOS pick-1-2-3 for the next meeting, or null. Admin-set; the database
   * keeps each rank unique and clears it when the issue is solved or archived.
   */
  rank: number | null
  /** Set when this issue was raised from a to-do (0019). Null otherwise. */
  task_id: string | null
  task_title: string | null
}

export interface NewIssue {
  description: string
  urgency: Urgency
  location: string | null
  department: string | null
  /** Naive `YYYY-MM-DDTHH:MM` from <input type="datetime-local">, or null. */
  occurredAt: string | null
  impact: string | null
  proposedSolution: string | null
  followUpRequired: boolean
  /**
   * Set when this issue is being dropped down from a headline (the EOS rule).
   * `create_issue` also marks that headline shared, since it has now served its
   * purpose at the meeting.
   */
  headlineId?: string | null
  /**
   * Set when this issue is being raised from a to-do (0019) — the Ninety
   * "linked issue". An admin or anyone assigned to that to-do may do it; the
   * database re-checks. The to-do itself is left untouched.
   */
  taskId?: string | null
}

function fail(error: { code?: string; message?: string }): Error {
  if (error.code === '28000') {
    return new Error('This link is not recognised. Ask Scott for a new one.')
  }
  if (error.code === '42501') {
    return new Error(error.message || 'You do not have permission to do that.')
  }
  if (error.code === '22023') {
    return new Error(error.message || 'That value is not allowed.')
  }
  return new Error(error.message || 'Something went wrong. Try again.')
}

export async function listIssues(
  token: string,
  includeArchived = false,
): Promise<Issue[]> {
  const { data, error } = await supabase.rpc('list_issues', {
    p_token: token,
    p_include_archived: includeArchived,
  })
  if (error) throw fail(error)
  return (data as Issue[] | null) ?? []
}

/**
 * Report an issue.
 *
 * `occurredAt` is sent as a plain STRING, deliberately. It arrives from
 * <input type="datetime-local"> as `YYYY-MM-DDTHH:MM` with no timezone on it.
 * Handing that to a `timestamptz` parameter would have Postgres read it as UTC
 * and file a 9am occurrence at 5am shop time. `create_issue` takes text and
 * attaches America/New_York itself — trap #7, at the RPC boundary where the
 * project's locked decisions say it belongs.
 */
export async function createIssue(token: string, issue: NewIssue): Promise<string> {
  const { data, error } = await supabase.rpc('create_issue', {
    p_token: token,
    p_description: issue.description,
    p_urgency: issue.urgency,
    p_location: issue.location,
    p_department: issue.department,
    p_occurred_at: issue.occurredAt,
    p_impact: issue.impact,
    p_proposed_solution: issue.proposedSolution,
    p_follow_up_required: issue.followUpRequired,
    p_headline_id: issue.headlineId ?? null,
    p_task_id: issue.taskId ?? null,
  })
  if (error) throw fail(error)
  return data as string
}

/**
 * Fix the reporter's own words — description, impact, proposed solution.
 *
 * Allowed for the reporter and for admins; the database enforces that. This is
 * the counterpart to `adminUpdateIssue`, which handles CLASSIFICATION (title,
 * area, urgency) and deliberately cannot touch these three fields.
 *
 * The list headline follows the corrected first line automatically — unless an
 * admin has already retitled it with Reclassify, in which case their choice is
 * kept. That decision is made in the database, not here.
 */
export async function updateIssueText(
  token: string,
  issueId: string,
  fields: { description: string; impact: string | null; proposedSolution: string | null },
): Promise<void> {
  const { error } = await supabase.rpc('update_issue_text', {
    p_token: token,
    p_issue_id: issueId,
    p_description: fields.description,
    p_impact: fields.impact,
    p_proposed_solution: fields.proposedSolution,
  })
  if (error) throw fail(error)
}

export async function adminSetIssueStatus(
  token: string,
  issueId: string,
  status: IssueStatus,
  resolutionNotes: string | null,
): Promise<void> {
  const { error } = await supabase.rpc('admin_set_issue_status', {
    p_token: token,
    p_issue_id: issueId,
    p_status: status,
    p_resolution_notes: resolutionNotes,
  })
  if (error) throw fail(error)
}

/**
 * Triage, not a rewrite. The database deliberately refuses to change the
 * description, impact or proposed solution: those are the reporter's own words
 * and the whole shop can read them.
 */
export async function adminUpdateIssue(
  token: string,
  issueId: string,
  fields: {
    title: string
    department: string
    location: string | null
    urgency: Urgency
    followUpRequired: boolean
  },
): Promise<void> {
  const { error } = await supabase.rpc('admin_update_issue', {
    p_token: token,
    p_issue_id: issueId,
    p_title: fields.title,
    p_department: fields.department,
    p_location: fields.location,
    p_urgency: fields.urgency,
    p_follow_up_required: fields.followUpRequired,
  })
  if (error) throw fail(error)
}

/**
 * Rank an issue 1, 2 or 3 for the next meeting, or null to clear it. Setting a
 * rank takes it from whichever issue held it — there is exactly one "2". The
 * database refuses to rank a Solved or archived issue.
 */
export async function adminRankIssue(
  token: string,
  issueId: string,
  rank: number | null,
): Promise<void> {
  const { error } = await supabase.rpc('admin_rank_issue', {
    p_token: token,
    p_issue_id: issueId,
    p_rank: rank,
  })
  if (error) throw fail(error)
}

export async function adminArchiveIssue(
  token: string,
  issueId: string,
  archived: boolean,
): Promise<void> {
  const { error } = await supabase.rpc('admin_archive_issue', {
    p_token: token,
    p_issue_id: issueId,
    p_archived: archived,
  })
  if (error) throw fail(error)
}

